import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Paper } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { deleteBscGoal, fetchProgramsByBusinessUnit, fetchProjectsByBusinessUnit } from "../../../api/companyApi";
import { useNotification } from "../../../providers/NotificationProvider";
import Alert from "@mui/material/Alert";
import DetailSection from "../../../components/common/DetailSection";
import DetailField from "../../../components/common/DetailField";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import { BSC_CATEGORY_CONFIG } from "./bscConfig";

const formatDate = (iso) => {
  if (!iso) return "";
  return iso.substring(0, 10).replace(/-/g, "/");
};

export default function BscGoalDetailPage() {
  const { businessDomainId, businessUnitId, category, goalIdx } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/bsc/${category}`;

  const config = BSC_CATEGORY_CONFIG[category];
  const [row, setRow] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isUsedByGoals, setIsUsedByGoals] = useState(false);

  const loadData = useCallback(async () => {
    if (!config) return;
    try {
      const data = await config.fetchGoals(businessUnitId);
      const item = Array.isArray(data) ? data[Number(goalIdx)] : null;
      setRow(item || null);
      // プログラム/プロジェクト目標での使用チェック
      const indicatorName = item?.indicator?.indicatorName;
      if (indicatorName) {
        const [programs, projects] = await Promise.all([
          fetchProgramsByBusinessUnit(businessUnitId).catch(() => []),
          fetchProjectsByBusinessUnit(businessUnitId).catch(() => []),
        ]);
        const usedByProgram = (Array.isArray(programs) ? programs : []).some((p) =>
          (p.goals || []).some((g) => g.indicator?.indicatorName === indicatorName)
        );
        const usedByProject = (Array.isArray(projects) ? projects : []).some((p) =>
          (p.goals || []).some((g) => g.indicator?.indicatorName === indicatorName)
        );
        setIsUsedByGoals(usedByProgram || usedByProject);
      }
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId, category, goalIdx]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteBscGoal(businessUnitId, row.goal);
      notify.success(t("message.delete.success"));
      navigate(basePath);
    } catch (e) { notify.apiError(e); }
  };

  if (!config) {
    return <Typography color="error">Unknown BSC category: {category}</Typography>;
  }
  if (!row) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t(config.titleKey)} - {t("common.detail")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            disabled={isUsedByGoals}
            onClick={() => navigate(`${basePath}/${goalIdx}/edit`)}
          >
            {t("common.edit")}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isUsedByGoals}
            onClick={() => setConfirmOpen(true)}
          >
            {t("common.delete")}
          </Button>
        </Box>
      </Box>
      {isUsedByGoals && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          このKPIはプログラムまたはプロジェクトの目標に使用されているため、編集・削除できません。
        </Alert>
      )}

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12} md={6}>
            <DetailField label={t("bsc.successFactor")} value={row.successFactorName || ""} />
          </Grid>
          <Grid size={12} md={6}>
            <DetailField
              label={t("bsc.indicator")}
              value={`${row.indicator?.indicatorName || ""}${row.indicator?.unit ? `（${row.indicator.unit}）` : ""}`}
            />
            {row.indicator && (
              <Paper variant="outlined" sx={{ mt: 1, p: 2, bgcolor: "grey.50" }}>
                {[
                  { label: t("appKpi.unit"),              value: row.indicator.unit },
                  { label: t("appKpi.measuringMethod"),    value: row.indicator.measuringMethod },
                  { label: t("appKpi.evaluationStandard"), value: row.indicator.evaluationStandard },
                  { label: t("appKpi.description"),        value: row.indicator.description },
                ].filter((r) => r.value).map((r) => (
                  <Box key={r.label} sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>{r.label}:</Typography>
                    <Typography variant="body2">{r.value}</Typography>
                  </Box>
                ))}
              </Paper>
            )}
          </Grid>
          <Grid size={12} md={4}>
            <DetailField label={t("bsc.plannedValue")} value={row.goal?.plannedValue != null ? String(row.goal.plannedValue) : ""} />
          </Grid>
          <Grid size={12} md={4}>
            <DetailField label={t("bsc.plannedDate")} value={formatDate(row.goal?.plannedDate)} />
          </Grid>
          <Grid size={12} md={4}>
            <DetailField label={t("bsc.deadline")} value={formatDate(row.goal?.deadline)} />
          </Grid>
        </Grid>
      </DetailSection>

      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<AssessmentIcon />}
          onClick={() => navigate(`${basePath}/${goalIdx}/achievements`)}
        >
          {t("bsc.achievement.list.title")}
        </Button>
        <Button variant="outlined" onClick={() => navigate(basePath)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
